import {
  Dashboard,
  DetailedCheckins,
  DetailedOrders,
  DetailedProductWithdrawals,
  DetailedSales,
  DetailedSessions,
} from '@linka/ui/pages/dashboard';
import { Skeleton } from '@linka/ui/skeleton';
import { t } from 'i18next';
import React, { Suspense } from 'react';
import { Navigate, Outlet, RouteObject } from 'react-router';
import DefaultLayout from '@/ui/components/default-layout';
import { ManageLayout } from '@/ui/components/manage-layout';
import { useSession } from '@/ui/hooks/use-session';
import { FormSkeleton } from '@/ui/pages/event/components/form-skeleton';
import { ListSkeleton } from '@/ui/pages/event/components/list-skeleton';
import { ErrorBoundary } from './error-boundary';

const DashboardPage = () => {
  const { session } = useSession();
  return <Dashboard userEmail={session.data?.user.email} />;
};

const DetailedSalesPage = () => {
  const { session } = useSession();
  return <DetailedSales userEmail={session.data?.user.email} />;
};

const DetailedCheckinsPage = () => {
  const { session } = useSession();
  return <DetailedCheckins userEmail={session.data?.user.email} />;
};

const DetailedProductWithdrawalsPage = () => {
  const { session } = useSession();
  return <DetailedProductWithdrawals userEmail={session.data?.user.email} />;
};

const DetailedOrdersPage = () => {
  const { session } = useSession();
  return <DetailedOrders userEmail={session.data?.user.email} />;
};

const DetailedSessionsPage = () => {
  const { session } = useSession();
  return <DetailedSessions userEmail={session.data?.user.email} />;
};

const Event = React.lazy(() => import('@/ui/pages/event').then((m) => ({ default: m.Event })));
const EventsList = React.lazy(() => import('@/ui/pages/event-list').then((m) => ({ default: m.EventsList })));
const AgendaList = React.lazy(() =>
  import('@/ui/pages/event/steps/agenda/components/agenda-list').then((m) => ({
    default: m.AgendaList,
  })),
);
const CreateAgendaForm = React.lazy(() =>
  import('@/ui/pages/event/steps/agenda/components/create-agenda-form').then((m) => ({ default: m.CreateAgendaForm })),
);
const EventDetails = React.lazy(() =>
  import('@/ui/pages/event/steps/details').then((m) => ({
    default: m.EventDetails,
  })),
);
const PaymentSettings = React.lazy(() =>
  import('@/ui/pages/event/steps/payment-settings').then((m) => ({
    default: m.PaymentSettings,
  })),
);
const CreateTicketForm = React.lazy(() =>
  import('@/ui/pages/event/steps/tickets/create-ticket-form/create-ticket-form').then((m) => ({
    default: m.CreateTicketForm,
  })),
);
const TicketTypeSelector = React.lazy(() =>
  import('@/ui/pages/event/steps/tickets/ticket-type-selector').then((m) => ({
    default: m.TicketTypeSelector,
  })),
);
const TicketsList = React.lazy(() =>
  import('@/ui/pages/event/steps/tickets/tickets-list').then((m) => ({
    default: m.TicketsList,
  })),
);
const Certificate = React.lazy(() =>
  import('@/ui/pages/manage/manage-participants/certificate/certificate').then((m) => ({ default: m.Certificate })),
);
const CertificateEditor = React.lazy(() =>
  import('@/ui/pages/manage/manage-participants/certificate/certificate-editor/certificate-editor').then((m) => ({
    default: m.CertificateEditor,
  })),
);
const CertificateDispatchConfig = React.lazy(() =>
  import('@/ui/pages/manage/manage-participants/certificate/dispatch-config/dispatch-config').then((m) => ({
    default: m.CertificateDispatchConfig,
  })),
);
const CertificateDeliveries = React.lazy(() =>
  import('@/ui/pages/manage/manage-participants/certificate/deliveries/deliveries').then((m) => ({
    default: m.CertificateDeliveries,
  })),
);
const CreateCoupon = React.lazy(() =>
  import('@/ui/pages/manage/manage-coupons/create-coupon/create-coupon').then((m) => ({ default: m.CreateCoupon })),
);
const ManageCoupons = React.lazy(() => import('@/ui/pages/manage/manage-coupons/manage-coupons'));
const ManageEmailCampaign = React.lazy(() =>
  import('@/ui/pages/manage/manage-email-campaign/manage-email-campaign').then((m) => ({
    default: m.ManageEmailCampaign,
  })),
);
const EmailCampaignConfig = React.lazy(() =>
  import('@/ui/pages/manage/manage-email-campaign/campaign-config/campaign-config').then((m) => ({
    default: m.EmailCampaignConfig,
  })),
);
const EmailCampaignTemplateEditor = React.lazy(() =>
  import('@/ui/pages/manage/manage-email-campaign/campaign-config/campaign-template-editor').then((m) => ({
    default: m.EmailCampaignTemplateEditor,
  })),
);
const ManageImportParticipants = React.lazy(() =>
  import('@/ui/pages/manage/manage-import-participants/import-participants').then((m) => ({
    default: m.ManageImportParticipants,
  })),
);
const ManageIntegrations = React.lazy(() =>
  import('@/ui/pages/manage/manage-integrations/manage-integrations').then((m) => ({ default: m.ManageIntegrations })),
);
const ManageIntegrationSkeleton = React.lazy(() =>
  import('@/ui/pages/manage/manage-integrations/manage-integrations').then((m) => ({
    default: m.ManageIntegrationSkeleton,
  })),
);
const ManageParticipants = React.lazy(() =>
  import('@/ui/pages/manage/manage-participants/manage-participants/manage-participants').then((m) => ({
    default: m.ManageParticipants,
  })),
);
const LabelConfiguration = React.lazy(() =>
  import('@/ui/pages/manage/manage-participants/label-configuration/label-configuration').then((m) => ({
    default: m.LabelConfiguration,
  })),
);
const TeamMembers = React.lazy(() =>
  import('@/ui/pages/manage/team-members/team-members').then((m) => ({
    default: m.TeamMembers,
  })),
);
const ProductsList = React.lazy(() =>
  import('@/ui/pages/manage/products/products-list').then((m) => ({
    default: m.ProductsList,
  })),
);
const CreateProductForm = React.lazy(() =>
  import('@/ui/pages/manage/products/create-product-form/create-product-form').then((m) => ({
    default: m.CreateProductForm,
  })),
);
const Sponsors = React.lazy(() => import('@/ui/pages/manage/sponsors/sponsors').then((m) => ({ default: m.Sponsors })));
const Settings = React.lazy(() => import('@/ui/pages/settings').then((m) => ({ default: m.Settings })));
const TermsAndPolitics = React.lazy(() =>
  import('@/ui/pages/terms-and-politics/terms-and-politics').then((m) => ({
    default: m.TermsAndPolitics,
  })),
);

export const manageRoutes: RouteObject[] = [
  {
    index: true,
    element: <Navigate to='dashboard' />,
  },
  {
    path: 'dashboard',
    element: <Outlet />,
    handle: {
      icon: 'bar-chart-big',
      crumb: () => t('dashboard-title'),
      label: t('dashboard-title'),
    },
    children: [
      {
        index: true,
        errorElement: <ErrorBoundary />,
        element: (
          <Suspense fallback={<Skeleton className='h-screen' />}>
            <DashboardPage />
          </Suspense>
        ),
      },
      {
        path: 'detailed-sales',
        element: <DetailedSalesPage />,
        handle: {
          crumb: () => t('dashboard.detailed.sales-data'),
        },
      },
      {
        path: 'detailed-checkins',
        element: <DetailedCheckinsPage />,
        handle: {
          crumb: () => t('dashboard.detailed.checkins-title'),
        },
      },
      {
        path: 'detailed-withdrawals',
        element: <DetailedProductWithdrawalsPage />,
        handle: {
          crumb: () => t('dashboard.detailed.withdrawals-title'),
        },
      },
      {
        path: 'detailed-order-status',
        element: <DetailedOrdersPage />,
        handle: {
          crumb: () => t('dashboard.detailed.orders-status'),
        },
      },
      {
        path: 'detailed-sessions',
        element: <DetailedSessionsPage />,
        handle: {
          crumb: () => t('dashboard.detailed.sessions-title'),
        },
      },
    ],
  },
  {
    path: 'participants',
    errorElement: <ErrorBoundary />,
    element: <Outlet />,
    handle: {
      icon: 'users-icon',
      label: t('manage-participants.title'),
    },
    children: [
      {
        errorElement: <ErrorBoundary />,
        element: (
          <Suspense fallback={<Skeleton className='h-screen' />}>
            <ManageParticipants />
          </Suspense>
        ),
        handle: {
          label: t('manage-participants.list.title'),
        },
        path: 'list-participants',
      },
      {
        errorElement: <ErrorBoundary />,
        element: (
          <Suspense fallback={<Skeleton className='h-screen' />}>
            <ManageImportParticipants />
          </Suspense>
        ),
        handle: {
          label: t('manage-participants.import.title'),
        },
        path: 'import-participants',
      },
      {
        errorElement: <ErrorBoundary />,
        element: (
          <Suspense fallback={<Skeleton className='h-screen' />}>
            <ManageParticipants
              description={t('belongings-description')}
              variant='belongings-control'
              title={t('belongings-title')}
            />
          </Suspense>
        ),
        handle: {
          label: t('belongings-title'),
        },
        path: 'belongings-control',
      },
      {
        errorElement: <ErrorBoundary />,
        element: (
          <Suspense fallback={<Skeleton className='h-screen' />}>
            <LabelConfiguration />
          </Suspense>
        ),
        handle: {
          label: t('label-config.title'),
        },
        path: 'label-configuration',
      },
      {
        errorElement: <ErrorBoundary />,
        element: <Outlet />,
        handle: {
          label: t('certificate.title'),
        },
        path: 'certificate',
        children: [
          {
            index: true,
            errorElement: <ErrorBoundary />,
            element: (
              <Suspense fallback={<Skeleton className='h-screen' />}>
                <Certificate />
              </Suspense>
            ),
          },
          {
            path: 'new',
            errorElement: <ErrorBoundary />,
            handle: { fullBleed: true },
            element: (
              <Suspense fallback={<FormSkeleton />}>
                <CertificateEditor />
              </Suspense>
            ),
          },
          {
            path: ':certificateId/edit',
            errorElement: <ErrorBoundary />,
            handle: { fullBleed: true },
            element: (
              <Suspense fallback={<FormSkeleton />}>
                <CertificateEditor />
              </Suspense>
            ),
          },
          {
            path: 'dispatch',
            errorElement: <ErrorBoundary />,
            handle: { label: t('certificate.dispatch.title') },
            element: (
              <Suspense fallback={<FormSkeleton />}>
                <CertificateDispatchConfig />
              </Suspense>
            ),
          },
          {
            path: 'deliveries',
            errorElement: <ErrorBoundary />,
            handle: { label: t('certificate.deliveries.breadcrumb') },
            element: (
              <Suspense fallback={<Skeleton className='h-screen' />}>
                <CertificateDeliveries />
              </Suspense>
            ),
          },
        ],
      },
    ],
  },
  {
    path: 'email-campaign',
    errorElement: <ErrorBoundary />,
    element: <Outlet />,
    handle: {
      icon: 'mail-icon',
      label: t('email-campaign.title'),
      crumb: () => t('email-campaign.title'),
    },
    children: [
      {
        index: true,
        errorElement: <ErrorBoundary />,
        element: (
          <Suspense fallback={<Skeleton className='h-screen' />}>
            <ManageEmailCampaign />
          </Suspense>
        ),
      },
      {
        // Antes de `:campaignId`, senão "new"/"configure" seriam lidos como id de campanha.
        path: 'new',
        errorElement: <ErrorBoundary />,
        handle: { fullBleed: true },
        element: (
          <Suspense fallback={<Skeleton className='h-screen' />}>
            <EmailCampaignTemplateEditor />
          </Suspense>
        ),
      },
      {
        // Destino do "Continuar" do editor para uma campanha nova, que ainda não tem id.
        path: 'configure',
        errorElement: <ErrorBoundary />,
        handle: {
          crumb: () => t('email-campaign.config.title'),
        },
        element: (
          <Suspense fallback={<Skeleton className='h-screen' />}>
            <EmailCampaignConfig />
          </Suspense>
        ),
      },
      {
        path: ':campaignId',
        errorElement: <ErrorBoundary />,
        handle: {
          crumb: () => t('email-campaign.config.title'),
        },
        element: (
          <Suspense fallback={<Skeleton className='h-screen' />}>
            <EmailCampaignConfig />
          </Suspense>
        ),
      },
      {
        path: ':campaignId/edit-template',
        errorElement: <ErrorBoundary />,
        handle: { fullBleed: true },
        element: (
          <Suspense fallback={<FormSkeleton />}>
            <EmailCampaignTemplateEditor />
          </Suspense>
        ),
      },
    ],
  },
  {
    path: 'coupons',
    errorElement: <ErrorBoundary />,
    element: <Outlet />,
    handle: {
      icon: 'ticket-percent-icon',
      label: t('manage-coupons.title'),
      crumb: () => t('manage-coupons.title'),
    },
    children: [
      {
        index: true,
        errorElement: <ErrorBoundary />,
        element: (
          <Suspense fallback={<Skeleton className='h-screen' />}>
            <ManageCoupons />
          </Suspense>
        ),
      },
      {
        path: 'new',
        handle: {
          crumb: () => t('create-coupon.title'),
        },
        errorElement: <ErrorBoundary />,
        element: (
          <Suspense fallback={<Skeleton className='h-screen' />}>
            <CreateCoupon />
          </Suspense>
        ),
      },
      {
        path: ':couponId/edit',
        handle: {
          crumb: () => t('edit-coupon.title'),
        },
        errorElement: <ErrorBoundary />,
        element: (
          <Suspense fallback={<Skeleton className='h-screen' />}>
            <CreateCoupon />
          </Suspense>
        ),
      },
    ],
  },
  {
    path: 'integrations',
    errorElement: <ErrorBoundary />,
    element: (
      <Suspense fallback={<ManageIntegrationSkeleton />}>
        <ManageIntegrations />
      </Suspense>
    ),
    handle: {
      crumb: () => t('integrations'),
      label: t('integrations'),
      icon: 'grid2x2Check',
    },
    index: true,
  },
  {
    path: 'team-members',
    errorElement: <ErrorBoundary />,
    element: (
      <Suspense fallback={<Skeleton className='h-screen' />}>
        <TeamMembers />
      </Suspense>
    ),
    handle: {
      icon: 'box',
      label: t('team-members.title'),
    },
  },
  {
    path: 'event',
    errorElement: <ErrorBoundary />,
    element: <Outlet />,
    handle: {
      icon: 'calendar-days-icon',
      label: t('event'),
    },
    children: [
      {
        path: 'products',
        errorElement: <ErrorBoundary />,
        element: <Outlet />,
        handle: {
          label: t('products'),
          crumb: () => t('products'),
        },
        children: [
          {
            index: true,
            errorElement: <ErrorBoundary />,
            element: (
              <Suspense fallback={<Skeleton className='h-screen' />}>
                <ProductsList />
              </Suspense>
            ),
          },
          {
            path: 'new',
            handle: {
              crumb: () => t('products.form.title'),
            },
            errorElement: <ErrorBoundary />,
            element: (
              <Suspense fallback={<Skeleton className='h-screen' />}>
                <CreateProductForm />
              </Suspense>
            ),
          },
          {
            path: ':productId/edit',
            handle: {
              crumb: () => t('products.form.title'),
            },
            errorElement: <ErrorBoundary />,
            element: (
              <Suspense fallback={<Skeleton className='h-screen' />}>
                <CreateProductForm />
              </Suspense>
            ),
          },
        ],
      },
      {
        path: 'sponsors',
        errorElement: <ErrorBoundary />,
        element: (
          <Suspense fallback={<Skeleton className='h-screen' />}>
            <Sponsors />
          </Suspense>
        ),
        handle: {
          label: t('sponsors.menu'),
        },
      },
    ],
  },
];

export default [
  {
    path: 'manage/:eventId',
    element: <ManageLayout />,
    children: manageRoutes,
  },
  {
    path: '/',
    element: <DefaultLayout />,
    children: [
      {
        path: 'events',
        errorElement: <ErrorBoundary />,
        element: (
          <Suspense fallback={<Skeleton className='h-screen' />}>
            <EventsList />
          </Suspense>
        ),
      },
      {
        path: 'events/:eventId',
        errorElement: <ErrorBoundary />,
        element: (
          <Suspense fallback={<Skeleton className='h-screen' />}>
            <Event />
          </Suspense>
        ),
        children: [
          {
            index: true,
            element: <Navigate to='details' />,
          },
          {
            path: 'details',
            errorElement: <ErrorBoundary />,
            element: (
              <Suspense fallback={<FormSkeleton />}>
                <EventDetails />
              </Suspense>
            ),
          },
          {
            path: 'agenda',
            errorElement: <ErrorBoundary />,
            element: <Outlet />,
            handle: {
              crumb: () => t('list-agenda.title'),
            },
            children: [
              {
                index: true,
                errorElement: <ErrorBoundary />,
                element: (
                  <Suspense fallback={<ListSkeleton />}>
                    <AgendaList />
                  </Suspense>
                ),
              },
              {
                path: ':agendaId/edit',
                errorElement: <ErrorBoundary />,
                element: (
                  <Suspense fallback={<FormSkeleton />}>
                    <CreateAgendaForm />
                  </Suspense>
                ),
                handle: {
                  crumb: () => t('agenda.edit'),
                },
              },
              {
                path: 'new',
                errorElement: <ErrorBoundary />,
                element: (
                  <Suspense fallback={<FormSkeleton />}>
                    <CreateAgendaForm />
                  </Suspense>
                ),
                handle: {
                  crumb: () => t('agenda.add'),
                },
              },
            ],
          },
          {
            path: 'tickets',
            errorElement: <ErrorBoundary />,
            element: <Outlet />,
            handle: {
              crumb: () => t('tickets'),
            },
            children: [
              {
                index: true,
                errorElement: <ErrorBoundary />,
                element: (
                  <Suspense fallback={<ListSkeleton />}>
                    <TicketsList />
                  </Suspense>
                ),
              },
              {
                path: ':ticketId/edit',
                errorElement: <ErrorBoundary />,
                element: (
                  <Suspense fallback={<FormSkeleton />}>
                    <CreateTicketForm />
                  </Suspense>
                ),
                handle: {
                  crumb: () => t('edit-ticket'),
                },
              },
              {
                path: 'new',
                errorElement: <ErrorBoundary />,
                element: (
                  <Suspense fallback={<FormSkeleton />}>
                    <TicketTypeSelector />
                  </Suspense>
                ),
                handle: {
                  crumb: () => t('add-ticket'),
                },
              },
            ],
          },
          {
            path: 'payment-settings',
            errorElement: <ErrorBoundary />,
            element: (
              <Suspense fallback={<FormSkeleton />}>
                <PaymentSettings />
              </Suspense>
            ),
          },
        ],
      },
      {
        path: 'settings',
        errorElement: <ErrorBoundary />,
        element: (
          <Suspense fallback={<Skeleton className='h-screen' />}>
            <Settings />
          </Suspense>
        ),
        handle: {
          crumb: () => t('settings.title'),
        },
        children: [
          {
            index: true,
            element: <Navigate to='terms-and-politics' replace />,
          },
          {
            path: 'terms-and-politics',
            errorElement: <ErrorBoundary />,
            element: (
              <Suspense fallback={<Skeleton className='h-screen' />}>
                <TermsAndPolitics />
              </Suspense>
            ),
            handle: {
              crumb: () => t('terms-and-politics'),
            },
          },
        ],
      },
    ],
  },
] as RouteObject[];
